import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Clock, Heart, Users, Award, Stethoscope, Building2, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import hero1 from "../assets/hospitalimg.png";
import hero2 from "../assets/staff.JPG";
import logo2 from "../assets/hospitalimg1.png";
import emergency from "../assets/emergencycare.png";
import lab from "../assets/lab.JPG";
import icu from "../assets/ICU.png";
import { useEffect, useState } from "react";

const Home = () => {

  const CountUp = ({ end, duration = 2 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const increment = end / (duration * 60); // 60fps

    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.ceil(start));
      }
    }, 1000 / 60);

    return () => clearInterval(timer);
  }, [end, duration]);

  return <>{count}</>;
};

  const heroImages = [hero1, hero2];

  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);

  React.useEffect(() => {
  const interval = setInterval(() => {
    setCurrentImageIndex((prev) =>
      prev === heroImages.length - 1 ? 0 : prev + 1
    );
  }, 5000);

  return () => clearInterval(interval);
}, [heroImages.length]);

  const features = [
    {
      icon: Shield,
      title: 'Qualified Staff',
      description: 'Highly skilled and experienced medical professionals',
      color: 'from-blue-400 to-sky-500',
    },
    {
      icon: Clock,
      title: '24/7 Emergency',
      description: 'Round-the-clock emergency and trauma care services',
      color: 'from-sky-400 to-cyan-500',
    },
    {
      icon: Heart,
      title: 'Affordable Care',
      description: 'Easy and affordable billing with cashless facility',
      color: 'from-cyan-400 to-teal-500',
    },
    {
      icon: Users,
      title: 'Patient First',
      description: 'Dedicated to providing compassionate patient care',
      color: 'from-teal-400 to-emerald-500',
    },
  ];

  const services = [
    {
      title: 'Emergency Care',
      description: '24/7 trauma care and emergency medical services',
      image: emergency,
      icon: Stethoscope,
    },
    {
      title: 'ICU Services',
      description: 'Advanced intensive care unit with modern equipment',
      image: icu,
      icon: Building2,
    },
    {
      title: 'Laboratory',
      description: 'Comprehensive diagnostic and testing facilities',
      image: lab,
      icon: Award,
    },
  ];

  const stats = [
  { value: 10, suffix: "+", label: "Years Experience" },
  { value: 8, suffix: "+", label: "Specialist Doctors" },
  { value: 24, suffix: "/7", label: "Emergency Care" },
  { value: 100, suffix: "%", label: "Patient Satisfaction" },
];


  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[70vh] md:h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image Slider */}
       <div className="absolute inset-0">
          {heroImages.map((img, index) => (
            <motion.div
              key={`${img}-${currentImageIndex}`}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: currentImageIndex === index ? 1 : 0, scale: 1 }}
              transition={{ duration: 1.2, ease: "easeInOut" }}
              className="absolute inset-0"
            >
              <img
                src={img}
                alt={`Hospital ${index + 1}`}
                className="w-full h-full object-cover object-[center_top] md:object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-sky-900/30 via-blue-900/40 to-transparent" />
            </motion.div>
          ))}
        </div>



        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-block mb-4 px-4 py-2 bg-white/20 backdrop-blur-md rounded-full text-white text-sm font-medium"
              >
                Trusted Healthcare Partner Since 2015
              </motion.div>
              <h1 className="text-5xl md:text-8xl font-bold text-white mb-6 leading-tight">
                Your Health,
                <span className="block text-blue-500">Our Priority</span>
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
                24hrs Trauma Care and Emergency Services with affordable healthcare for all
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button className="bg-sky-500 hover:bg-sky-600 text-white px-8 py-6 text-lg rounded-full shadow-2xl hover:shadow-sky-500/50 hover:scale-105 transition-all">
                    Book Appointment
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button
                    variant="outline"
                    className="bg-white/20 backdrop-blur-md border-2 border-white text-white hover:bg-white hover:text-sky-600 px-8 py-6 text-lg rounded-full shadow-xl"
                  >
                    Our Services
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white rounded-full flex items-start justify-center p-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-white rounded-full"
            ></motion.div>
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-b from-white to-sky-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="border-none shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2 bg-white">
                  <CardContent className="p-8 text-center">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className={`w-16 h-16 mx-auto mb-6 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center shadow-lg`}
                    >
                      <feature.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    <h3 className="text-xl font-bold mb-3 text-gray-800">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative">
                <img
                  src={logo2}
                  alt="Hospital Building"
                  className="rounded-3xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-sky-500 text-white p-6 rounded-2xl shadow-xl">
                  <p className="text-4xl font-bold">10+</p>
                  <p className="text-sm">Years of Service</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-800">
                Caring for Your Health Since
                <span className="text-sky-500"> 2015</span>
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Ashoka Hospital was founded with a mission to provide quality healthcare
                facilities at affordable prices. Our
                state-of-the-art facility features wide corridors, good light, fresh air,
                spacious wards, and special rooms.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-sky-100 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-sky-500 rounded-full"></div>
                  </div>
                  <span className="text-gray-700">Expert Team of Specialists</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-sky-100 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-sky-500 rounded-full"></div>
                  </div>
                  <span className="text-gray-700">Modern Medical Equipment</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-sky-100 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-sky-500 rounded-full"></div>
                  </div>
                  <span className="text-gray-700">Compassionate Patient Care</span>
                </li>
              </ul>
              <Link to="/about">
                <Button className="bg-sky-500 hover:bg-sky-600 text-white px-8 py-6 text-lg rounded-full shadow-lg">
                  Learn More About Us
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-gradient-to-b from-sky-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800">
              Our <span className="text-sky-500">Services</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive healthcare services designed to meet all your medical needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                <Card className="overflow-hidden border-none shadow-xl hover:shadow-2xl transition-all h-full">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <service.icon className="w-8 h-8 text-sky-400 mb-2" />
                      <h3 className="text-2xl font-bold text-white">{service.title}</h3>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button
                variant="outline"
                className="border-2 border-sky-500 text-sky-500 hover:bg-sky-500 hover:text-white px-8 py-6 text-lg rounded-full"
              >
                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-sky-500 via-blue-500 to-sky-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <h3 className="text-5xl md:text-6xl font-bold mb-2">
                  <CountUp end={stat.value} duration={2} />
                  {stat.suffix}
                </h3>
                <p className="text-lg text-white/90">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-sky-500 to-blue-600 rounded-3xl p-12 md:p-16 text-white text-center shadow-2xl relative overflow-hidden"
          >
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
            </div>

            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Need Medical Assistance?
              </h2>
              <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
                Our team is available 24/7 for emergency care. Book an appointment or contact us for immediate assistance.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link to="/contact">
                  <Button className="bg-white text-sky-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full shadow-xl hover:scale-105 transition-all">
                    Book Appointment
                  </Button>
                </Link>
                <a href="0836-2373778">
                  <Button
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-sky-600 px-8 py-6 text-lg rounded-full"
                  >
                    <Phone className="mr-2 w-5 h-5" />
                    Emergency Call
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;